/**
 * SearchableDropdown - Reusable searchable dropdown component
 * Use for filtering dialers, companies, etc.
 */

import { useState, useEffect, useMemo, useRef } from 'react'
import { Search, ChevronDown, X } from 'lucide-react'
import './SearchableDropdown.css'

export default function SearchableDropdown({
    options,
    value,
    onChange,
    placeholder = 'Search...',
    allLabel = 'All',
    labelKey = 'name',
    valueKey = 'id'
}) {
    const [isOpen, setIsOpen] = useState(false)
    const [search, setSearch] = useState('')
    const dropdownRef = useRef(null)
    const inputRef = useRef(null)

    const selectedOption = options.find(opt => String(opt[valueKey]) === String(value))

    const filteredOptions = useMemo(() => {
        if (!search) return options
        const q = search.toLowerCase()
        return options.filter(opt => opt[labelKey]?.toLowerCase().includes(q))
    }, [options, search, labelKey])

    // Close on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setIsOpen(false)
                setSearch('')
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    // Focus input when opening
    useEffect(() => {
        if (isOpen && inputRef.current) {
            inputRef.current.focus()
        }
    }, [isOpen])

    const handleSelect = (optValue) => {
        onChange(optValue)
        setIsOpen(false)
        setSearch('')
    }

    return (
        <div className="searchable-dropdown" ref={dropdownRef}>
            <button
                type="button"
                className={`dropdown-trigger ${isOpen ? 'open' : ''}`}
                onClick={() => setIsOpen(!isOpen)}
            >
                <span className="dropdown-value">
                    {selectedOption ? selectedOption[labelKey] : allLabel}
                </span>
                <ChevronDown size={14} className={`dropdown-chevron ${isOpen ? 'rotated' : ''}`} />
            </button>

            {isOpen && (
                <div className="dropdown-menu">
                    <div className="dropdown-search">
                        <Search size={14} />
                        <input
                            ref={inputRef}
                            type="text"
                            placeholder={placeholder}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                        {search && (
                            <button className="clear-search" onClick={() => setSearch('')}>
                                <X size={12} />
                            </button>
                        )}
                    </div>
                    <div className="dropdown-options">
                        <div
                            className={`dropdown-option ${!value ? 'selected' : ''}`}
                            onClick={() => handleSelect('')}
                        >
                            {allLabel}
                        </div>
                        {filteredOptions.length === 0 ? (
                            <div className="dropdown-empty">No results found</div>
                        ) : (
                            filteredOptions.map(opt => (
                                <div
                                    key={opt[valueKey]}
                                    className={`dropdown-option ${String(opt[valueKey]) === String(value) ? 'selected' : ''}`}
                                    onClick={() => handleSelect(opt[valueKey])}
                                >
                                    {opt[labelKey]}
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    )
}
